import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, setElevation, setFontVariant } from "@similarweb/styles";
import { Flex, Stack, Text, type TextProps } from "@similarweb/ui-components/foundations";

export const StyledScoringBoard = styled(Flex)`
    width: 100%;
    gap: 16px;
    align-items: stretch;
`;

export const StyledScoringPriorityColumn = styled(Stack)`
    flex: 1;
    min-width: 0;
    min-height: calc(100vh - 280px);
    gap: 12px;
    padding: 16px;
    background: ${colorsPalettes.carbon["0"]};
    border: 1px solid ${colorsPalettes.midnight["50"]};
    border-radius: 10px;
`;

export const StyledColumnHeader = styled(Flex)`
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

export const StyledColumnEmptyState = styled(Flex)`
    width: 100%;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 12px 16px;
    border: 1px dashed ${colorsPalettes.carbon["100"]};
    border-radius: 8px;
    background: ${colorsPalettes.bluegrey["100"]};
    ${setFontVariant("body2", colorsPalettes.carbon["300"])};
`;

type RuleCardStateProps = { $isSelected?: boolean; $isSelectable?: boolean; $isDisabled?: boolean };

const ruleCardBackground = ({ $isSelected, $isDisabled }: RuleCardStateProps): string => {
    if ($isDisabled) {
        return colorsPalettes.bluegrey["100"];
    }

    return $isSelected ? colorsPalettes.blue["50"] : colorsPalettes.carbon["0"];
};

export const StyledRuleCard = styled(Stack)<RuleCardStateProps>`
    width: 100%;
    gap: 12px;
    padding: 16px;
    background: ${ruleCardBackground};
    border: 1px solid
        ${({ $isSelected, $isDisabled }) =>
            $isSelected && !$isDisabled ? colorsPalettes.blue["400"] : colorsPalettes.midnight["50"]};
    border-radius: 8px;
    ${setElevation("Z1")}
    opacity: ${({ $isDisabled }) => ($isDisabled ? 0.6 : 1)};
    cursor: ${({ $isSelectable, $isDisabled }) => ($isSelectable && !$isDisabled ? "pointer" : "default")};
`;

export const StyledRuleCardHeader = styled(Stack)`
    width: 100%;
    gap: 2px;
`;

export const StyledRuleCardContextRow = styled(Flex)`
    width: 100%;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px;
`;

export const StyledRuleCardContextText = styled(Text).attrs<Partial<TextProps>>({
    as: "div",
    fontVariant: "subTitle3",
})`
    flex: 1;
    min-width: 0;
    overflow-wrap: break-word;
    ${setFontVariant("subTitle3", colorsPalettes.carbon["500"])};
`;

export const StyledRuleCardActionItemText = styled(Text).attrs<Partial<TextProps>>({
    as: "div",
    fontVariant: "body2",
})`
    width: 100%;
    overflow-wrap: break-word;
    ${setFontVariant("body2", colorsPalettes.carbon["300"])};
`;

export const StyledRuleCardActions = styled(Flex)`
    gap: 4px;
    align-items: center;
    flex-shrink: 0;

    & svg path {
        fill-opacity: 1;
    }
`;

export const StyledExpandableFilterChip = styled(Flex)`
    display: inline-flex;
    align-self: flex-start;
    cursor: pointer;

    &:hover > * {
        background: ${colorsPalettes.midnight[50]};

        ${SWReactIcons} {
            svg path {
                fill: ${colorsPalettes.midnight[400]};
            }
        }
    }
`;

export const StyledDraggableRuleCardWrapper = styled.div<{ $isDragging?: boolean }>`
    position: relative;
    width: 100%;
    opacity: ${({ $isDragging }) => ($isDragging ? 0.35 : 1)};

    ${StyledRuleCard} {
        padding-left: 38px;
    }
`;

export const StyledScoringRuleDragHandle = styled(Flex)`
    position: absolute;
    left: 8px;
    top: 50%;
    transform: translateY(-50%);
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 32px;
    opacity: 0;
    cursor: grab;
    z-index: 1;
    border-radius: 4px;
    background: transparent;
    transition:
        opacity 150ms ease,
        background-color 150ms ease;

    ${StyledDraggableRuleCardWrapper}:hover & {
        opacity: 1;
        background: ${colorsPalettes.bluegrey[100]};
    }

    &:active {
        cursor: grabbing;
    }
`;

export const StyledColumnDroppableArea = styled.div<{ $isActiveDropTarget?: boolean }>`
    position: relative;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
    box-sizing: border-box;
    border-radius: 8px;
    border: 2px dashed ${({ $isActiveDropTarget }) => ($isActiveDropTarget ? colorsPalettes.blue[400] : "transparent")};
    transition: border-color 150ms ease;
`;

export const StyledColumnRulesContent = styled(Stack)<{ $isBlurred?: boolean }>`
    gap: 12px;
    filter: ${({ $isBlurred }) => ($isBlurred ? "blur(2px)" : "none")};
    pointer-events: ${({ $isBlurred }) => ($isBlurred ? "none" : "auto")};
    transition: filter 150ms ease;
`;

export const StyledScoringRuleDropZone = styled(Flex)`
    position: absolute;
    inset: 0;
    z-index: 2;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
    pointer-events: none;
    border-radius: 8px;
    background: ${colorsPalettes.blue[50]};
`;

export const StyledDropZoneCircle = styled(Flex)`
    width: 40px;
    height: 40px;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: ${colorsPalettes.blue[400]};

    ${SWReactIcons} svg path {
        fill: ${colorsPalettes.carbon[0]};
    }
`;

export const StyledDragOverlayCard = styled.div<{ $width: number }>`
    width: ${({ $width }) => ($width > 0 ? `${$width}px` : "100%")};
    cursor: grabbing;
    ${setElevation("Z2")}
`;

/**
 * Matches the circle `AvatarName` draws for a team, which cannot be reused here because it renders
 * initials rather than arbitrary text.
 */
export const StyledAllTeamsAvatarCircle = styled(Flex)`
    width: 28px;
    height: 28px;
    min-width: 28px;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    border: 1px solid ${colorsPalettes.carbon[100]};
    background: ${colorsPalettes.carbon[0]};
    cursor: default;
    ${setFontVariant("captionTitle", colorsPalettes.carbon[300])};
    transition:
        border-color 0.2s ease,
        color 0.2s ease;

    &:hover {
        border-color: ${colorsPalettes.carbon[300]};
        color: ${colorsPalettes.carbon[400]};
    }
`;

export const StyledColumnRulesCount = styled(Text).attrs<Partial<TextProps>>({
    as: "span",
    fontVariant: "caption",
})`
    ${setFontVariant("caption", colorsPalettes.carbon[300])};
`;
